import React from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
    Gift, CheckCircle, ArrowRight, Building2,
    Smartphone, UserPlus, Send, Clock,
    Shield, Zap, Heart, Star,
    FileText, Calendar, Search, MapPin, Users, Bell,
    Link2, MessageSquare, TrendingUp
} from 'lucide-react';

function IvanPromoLanding() {
    const navigate = useNavigate();
    const promoUrl = 'https://www.dentalcluster.com/promo/ivan';
    const labId = '76Y6wwTXUZcN7lbiHds3ZYSQcK92';
    const labName = '아이반치과기공소';

    const handleSignUp = () => {
        navigate(`/signup?promo=ivan&lab=${labId}&type=clinic`);
    };

    return (
        <div style={styles.container}>
            {/* ========== 1. Hero 섹션 ========== */}
            <section style={styles.heroSection}>
                <div style={styles.heroContent}>
                    <div style={styles.qrCard}>
                        <QRCodeSVG value={promoUrl} size={100} level="H" includeMargin={true} />
                        <p style={styles.qrText}>QR코드로 접속</p>
                    </div>

                    <h1 style={styles.heroTitle}>
                        치과 업무,<br />
                        <span style={styles.highlight}>이제 스마트하게!</span>
                    </h1>

                    <div style={styles.heroBadges}>
                        <span style={styles.heroBadge}>
                            <FileText size={16} /> 의뢰서 관리
                        </span>
                        <span style={styles.heroBadge}>
                            <Link2 size={16} /> 기공소 매칭
                        </span>
                    </div>

                    <p style={styles.heroSubtitle}>
                        기공 의뢰서 작성부터 기공소 찾기까지<br />
                        DentalCluster 하나로 해결하세요
                    </p>

                    <button style={styles.ctaButton} onClick={handleSignUp}>
                        <UserPlus size={20} />
                        무료 회원가입
                        <ArrowRight size={18} />
                    </button>

                    <p style={styles.ctaSubtext}>
                        <CheckCircle size={14} />
                        가입 즉시 {labName}와 연결!
                    </p>
                </div>
            </section>

            {/* ========== 2. 두 핵심 기능 소개 ========== */}
            <section style={styles.coreSection}>
                <h2 style={styles.sectionTitle}>DentalCluster 핵심 기능</h2>
                <p style={styles.sectionSubtitle}>치과 업무를 혁신하는 두 가지 핵심 서비스</p>

                <div style={styles.coreGrid}>
                    {/* 의뢰서 관리 */}
                    <div style={styles.coreCard}>
                        <div style={{...styles.coreIcon, background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'}}>
                            <FileText size={32} color="#fff" />
                        </div>
                        <h3 style={styles.coreTitle}>의뢰서 관리</h3>
                        <p style={styles.coreDesc}>
                            클릭 몇 번으로 기공 의뢰서 작성<br />
                            실시간 진행 상태 확인
                        </p>
                        <div style={styles.coreFeatures}>
                            <span style={styles.coreFeatureTag}><Zap size={12} /> 빠른 작성</span>
                            <span style={styles.coreFeatureTag}><Clock size={12} /> 실시간 현황</span>
                            <span style={styles.coreFeatureTag}><Bell size={12} /> 마감 알림</span>
                        </div>
                    </div>

                    {/* 기공소 매칭 */}
                    <div style={styles.coreCard}>
                        <div style={{...styles.coreIcon, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
                            <Link2 size={32} color="#fff" />
                        </div>
                        <h3 style={styles.coreTitle}>기공소 매칭</h3>
                        <p style={styles.coreDesc}>
                            전국 기공소 검색 및 비교<br />
                            원하는 기공소와 바로 연결
                        </p>
                        <div style={styles.coreFeatures}>
                            <span style={{...styles.coreFeatureTag, backgroundColor: '#d1fae5', color: '#065f46'}}><Search size={12} /> 검색</span>
                            <span style={{...styles.coreFeatureTag, backgroundColor: '#d1fae5', color: '#065f46'}}><MapPin size={12} /> 지역별</span>
                            <span style={{...styles.coreFeatureTag, backgroundColor: '#d1fae5', color: '#065f46'}}><Users size={12} /> 비교</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== 3. 의뢰서 관리 상세 ========== */}
            <section style={styles.featureSection}>
                <div style={styles.featureHeader}>
                    <div style={{...styles.featureIconBig, backgroundColor: '#eef2ff'}}>
                        <FileText size={28} color="#6366f1" />
                    </div>
                    <div>
                        <h2 style={styles.featureSectionTitle}>의뢰서 관리 시스템</h2>
                        <p style={styles.featureSectionDesc}>복잡한 기공 주문을 간편하게</p>
                    </div>
                </div>

                <div style={styles.orderDemoContainer}>
                    {/* 의뢰서 작성 과정 */}
                    <div style={styles.orderSteps}>
                        <div style={styles.orderStep}>
                            <div style={styles.orderStepNum}>1</div>
                            <div style={styles.orderStepContent}>
                                <strong>환자 정보</strong>
                                <div style={styles.orderStepDemo}>
                                    <span>홍길동</span>
                                    <span>2024-0123</span>
                                </div>
                            </div>
                        </div>
                        <div style={styles.orderStep}>
                            <div style={styles.orderStepNum}>2</div>
                            <div style={styles.orderStepContent}>
                                <strong>치아 선택</strong>
                                <div style={styles.toothMini}>
                                    {[14,15,16,24,25].map(t => (
                                        <span key={t} style={styles.toothSelected}>{t}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div style={styles.orderStep}>
                            <div style={styles.orderStepNum}>3</div>
                            <div style={styles.orderStepContent}>
                                <strong>보철물 선택</strong>
                                <div style={styles.prostheticMini}>
                                    <CheckCircle size={14} color="#10b981" />
                                    <span>지르코니아 크라운</span>
                                </div>
                            </div>
                        </div>
                        <div style={styles.orderStep}>
                            <div style={{...styles.orderStepNum, backgroundColor: '#10b981'}}>✓</div>
                            <div style={styles.orderStepContent}>
                                <strong>전송 완료!</strong>
                                <p style={styles.orderStepNote}>기공소로 즉시 전달</p>
                            </div>
                        </div>
                    </div>

                    {/* 의뢰서 관리 기능들 */}
                    <div style={styles.orderFeatureGrid}>
                        <div style={styles.orderFeatureItem}>
                            <Calendar size={20} color="#6366f1" />
                            <span>캘린더 일정</span>
                        </div>
                        <div style={styles.orderFeatureItem}>
                            <Bell size={20} color="#f59e0b" />
                            <span>마감 알림</span>
                        </div>
                        <div style={styles.orderFeatureItem}>
                            <MessageSquare size={20} color="#10b981" />
                            <span>실시간 채팅</span>
                        </div>
                        <div style={styles.orderFeatureItem}>
                            <TrendingUp size={20} color="#8b5cf6" />
                            <span>통계 분석</span>
                        </div>
                    </div>
                </div>

                <div style={styles.featureBenefits}>
                    <div style={styles.benefitTag}><Clock size={14} /> 작성 1분</div>
                    <div style={styles.benefitTag}><Smartphone size={14} /> 모바일 OK</div>
                    <div style={styles.benefitTag}><Shield size={14} /> 자동 저장</div>
                </div>
            </section>

            {/* ========== 4. 기공소 매칭 상세 ========== */}
            <section style={{...styles.featureSection, backgroundColor: '#f0fdf4'}}>
                <div style={styles.featureHeader}>
                    <div style={{...styles.featureIconBig, backgroundColor: '#d1fae5'}}>
                        <Link2 size={28} color="#059669" />
                    </div>
                    <div>
                        <h2 style={styles.featureSectionTitle}>기공소 매칭 서비스</h2>
                        <p style={styles.featureSectionDesc}>딱 맞는 기공소를 찾아드립니다</p>
                    </div>
                </div>

                <div style={styles.matchingDemo}>
                    {/* 검색 조건 */}
                    <div style={styles.matchingSearch}>
                        <div style={styles.matchingSearchHeader}>
                            <Search size={18} color="#059669" />
                            <span>기공소 검색</span>
                        </div>
                        <div style={styles.matchingFilters}>
                            <span style={styles.matchingFilter}><MapPin size={12} /> 서울</span>
                            <span style={styles.matchingFilter}><Building2 size={12} /> 지르코니아</span>
                            <span style={styles.matchingFilter}><Star size={12} /> 평점순</span>
                        </div>
                    </div>

                    {/* 검색 결과 */}
                    <div style={styles.matchingResults}>
                        <div style={styles.matchingLabCard}>
                            <div style={styles.matchingLabAvatar}>A</div>
                            <div style={styles.matchingLabInfo}>
                                <strong>A 치과기공소</strong>
                                <span>서울 강남 · 지르코니아 전문</span>
                            </div>
                            <button style={styles.matchingConnectBtn}>연결</button>
                        </div>
                        <div style={styles.matchingLabCard}>
                            <div style={{...styles.matchingLabAvatar, backgroundColor: '#8b5cf6'}}>B</div>
                            <div style={styles.matchingLabInfo}>
                                <strong>B 덴탈랩</strong>
                                <span>서울 서초 · CAD/CAM</span>
                            </div>
                            <button style={styles.matchingConnectBtn}>연결</button>
                        </div>
                        <div style={styles.matchingLabCard}>
                            <div style={{...styles.matchingLabAvatar, backgroundColor: '#f59e0b'}}>C</div>
                            <div style={styles.matchingLabInfo}>
                                <strong>C 기공소</strong>
                                <span>경기 성남 · 임플란트</span>
                            </div>
                            <button style={styles.matchingConnectBtn}>연결</button>
                        </div>
                    </div>
                </div>

                <div style={styles.matchingBenefits}>
                    <div style={styles.matchingBenefitItem}>
                        <Search size={20} color="#059669" />
                        <div>
                            <strong>전국 기공소 검색</strong>
                            <p>지역, 전문 분야별 검색</p>
                        </div>
                    </div>
                    <div style={styles.matchingBenefitItem}>
                        <Users size={20} color="#059669" />
                        <div>
                            <strong>비교 선택</strong>
                            <p>여러 기공소 비교 후 선택</p>
                        </div>
                    </div>
                    <div style={styles.matchingBenefitItem}>
                        <Send size={20} color="#059669" />
                        <div>
                            <strong>간편 연결</strong>
                            <p>클릭 한 번으로 연결 요청</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== 5. 사용방법 ========== */}
            <section style={styles.stepsSection}>
                <h2 style={styles.sectionTitle}>간단한 시작</h2>
                <div style={styles.stepsContainer}>
                    <div style={styles.stepCard}>
                        <div style={styles.stepNumber}>1</div>
                        <UserPlus size={28} color="#6366f1" />
                        <h3>회원가입</h3>
                        <p>1분 만에 가입 완료</p>
                    </div>
                    <div style={styles.stepArrow}><ArrowRight size={20} color="#cbd5e1" /></div>
                    <div style={styles.stepCard}>
                        <div style={styles.stepNumber}>2</div>
                        <Link2 size={28} color="#6366f1" />
                        <h3>기공소 연결</h3>
                        <p>매칭 또는 직접 연결</p>
                    </div>
                    <div style={styles.stepArrow}><ArrowRight size={20} color="#cbd5e1" /></div>
                    <div style={styles.stepCard}>
                        <div style={styles.stepNumber}>3</div>
                        <Smartphone size={28} color="#6366f1" />
                        <h3>주문 시작</h3>
                        <p>언제 어디서나 관리</p>
                    </div>
                </div>
            </section>

            {/* ========== 6. 아이반 기공소 + 매칭 ========== */}
            <section style={styles.labSection}>
                <div style={styles.labGrid}>
                    {/* 아이반 기공소 */}
                    <div style={styles.labCard}>
                        <div style={styles.labBadge}>
                            <Star size={12} fill="#fbbf24" color="#fbbf24" />
                            추천 기공소
                        </div>
                        <div style={styles.labHeader}>
                            <div style={styles.labAvatar}>
                                <Building2 size={24} color="#6366f1" />
                            </div>
                            <h3 style={styles.labName}>{labName}</h3>
                        </div>
                        <div style={styles.labStrengths}>
                            <div style={styles.labStrengthItem}>
                                <CheckCircle size={14} color="#10b981" />
                                <span>20년 이상 기공 경력</span>
                            </div>
                            <div style={styles.labStrengthItem}>
                                <CheckCircle size={14} color="#10b981" />
                                <span>최신 CAD/CAM 장비</span>
                            </div>
                            <div style={styles.labStrengthItem}>
                                <CheckCircle size={14} color="#10b981" />
                                <span>빠르고 정확한 납품</span>
                            </div>
                        </div>
                        <div style={styles.labCta}>
                            가입 시 <strong>{labName}</strong>에서<br />직접 연결 요청을 보내드립니다!
                        </div>
                    </div>

                    {/* 매칭 서비스 */}
                    <div style={{...styles.labCard, backgroundColor: '#f0fdf4', border: '2px solid #86efac'}}>
                        <div style={{...styles.labBadge, backgroundColor: '#d1fae5', color: '#065f46'}}>
                            <Link2 size={12} />
                            매칭 서비스
                        </div>
                        <div style={styles.labHeader}>
                            <div style={{...styles.labAvatar, backgroundColor: '#d1fae5'}}>
                                <Search size={24} color="#059669" />
                            </div>
                            <h3 style={styles.labName}>더 많은 기공소</h3>
                        </div>
                        <p style={styles.matchingDesc}>
                            전국의 다양한 기공소를<br />
                            직접 검색하고 비교하세요!
                        </p>
                        <div style={styles.matchingPreview}>
                            <div style={styles.matchingPreviewItem}>
                                <MapPin size={14} color="#059669" />
                                <span>지역별 검색</span>
                            </div>
                            <div style={styles.matchingPreviewItem}>
                                <Building2 size={14} color="#059669" />
                                <span>분야별 검색</span>
                            </div>
                            <div style={styles.matchingPreviewItem}>
                                <Send size={14} color="#059669" />
                                <span>바로 연결</span>
                            </div>
                        </div>
                        <div style={{...styles.labCta, backgroundColor: '#d1fae5', color: '#065f46'}}>
                            회원가입 후 무료로 이용 가능
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== 7. 가입 혜택 ========== */}
            <section style={styles.benefitsSection}>
                <h2 style={styles.sectionTitle}>가입 혜택</h2>
                <div style={styles.benefitsList}>
                    <div style={styles.benefitItem}>
                        <Heart size={24} color="#ef4444" />
                        <div>
                            <strong>완전 무료</strong>
                            <p>가입비, 이용료 없음</p>
                        </div>
                    </div>
                    <div style={styles.benefitItem}>
                        <Zap size={24} color="#f59e0b" />
                        <div>
                            <strong>즉시 사용</strong>
                            <p>가입 후 바로 이용</p>
                        </div>
                    </div>
                    <div style={styles.benefitItem}>
                        <Shield size={24} color="#10b981" />
                        <div>
                            <strong>안전한 관리</strong>
                            <p>체계적인 주문 시스템</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== 8. 최종 CTA ========== */}
            <section style={styles.finalCtaSection}>
                <div style={styles.finalCtaCard}>
                    <h2 style={styles.finalCtaTitle}>지금 바로 시작하세요!</h2>
                    <p style={styles.finalCtaText}>
                        의뢰서 관리부터 기공소 매칭까지<br />
                        DentalCluster와 함께 스마트한 치과 업무를 경험하세요
                    </p>

                    <button style={styles.finalCtaButton} onClick={handleSignUp}>
                        <Gift size={22} />
                        무료 회원가입
                        <ArrowRight size={20} />
                    </button>

                    <div style={styles.finalQrBox}>
                        <QRCodeSVG value={promoUrl} size={80} level="H" includeMargin={true} />
                        <p>모바일에서 QR 스캔</p>
                    </div>
                </div>
            </section>

            {/* 푸터 */}
            <footer style={styles.footer}>
                <p style={styles.footerText}>DentalCluster</p>
                <p style={styles.footerSubtext}>치과와 기공소를 연결하는 스마트 플랫폼</p>
            </footer>
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
    },
    // Hero
    heroSection: {
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        padding: '40px 24px 50px',
        textAlign: 'center',
    },
    heroContent: {
        maxWidth: '500px',
        margin: '0 auto',
    },
    qrCard: {
        display: 'inline-block',
        backgroundColor: 'white',
        padding: '12px',
        borderRadius: '16px',
        marginBottom: '20px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
    },
    qrText: {
        margin: '6px 0 0 0',
        fontSize: '11px',
        color: '#64748b',
        fontWeight: '600',
    },
    heroTitle: {
        fontSize: '28px',
        fontWeight: '800',
        color: 'white',
        lineHeight: 1.3,
        marginBottom: '16px',
    },
    highlight: {
        color: '#fbbf24',
    },
    heroBadges: {
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        marginBottom: '16px',
    },
    heroBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 14px',
        backgroundColor: 'rgba(255,255,255,0.2)',
        borderRadius: '20px',
        color: 'white',
        fontSize: '13px',
        fontWeight: '600',
    },
    heroSubtitle: {
        fontSize: '15px',
        color: 'rgba(255,255,255,0.9)',
        lineHeight: 1.6,
        marginBottom: '24px',
    },
    ctaButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        padding: '16px 32px',
        fontSize: '17px',
        fontWeight: '700',
        color: '#6366f1',
        backgroundColor: 'white',
        border: 'none',
        borderRadius: '14px',
        cursor: 'pointer',
        boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
    },
    ctaSubtext: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        color: 'rgba(255,255,255,0.9)',
        fontSize: '13px',
        marginTop: '16px',
    },
    // Core Section
    coreSection: {
        padding: '50px 24px',
        backgroundColor: '#fff',
    },
    sectionTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#1e293b',
        textAlign: 'center',
        marginBottom: '8px',
    },
    sectionSubtitle: {
        fontSize: '15px',
        color: '#64748b',
        textAlign: 'center',
        marginBottom: '32px',
    },
    coreGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '16px',
        maxWidth: '600px',
        margin: '0 auto',
    },
    coreCard: {
        backgroundColor: '#f8fafc',
        borderRadius: '20px',
        padding: '24px 20px',
        textAlign: 'center',
    },
    coreIcon: {
        width: '64px',
        height: '64px',
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px',
    },
    coreTitle: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '8px',
    },
    coreDesc: {
        fontSize: '13px',
        color: '#64748b',
        lineHeight: 1.5,
        marginBottom: '16px',
    },
    coreFeatures: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '6px',
    },
    coreFeatureTag: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        backgroundColor: '#eef2ff',
        color: '#4f46e5',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
    },
    // Feature Section
    featureSection: {
        padding: '50px 24px',
        backgroundColor: '#f8fafc',
    },
    featureHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        maxWidth: '600px',
        margin: '0 auto 32px',
    },
    featureIconBig: {
        width: '56px',
        height: '56px',
        borderRadius: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    featureSectionTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1e293b',
        margin: 0,
    },
    featureSectionDesc: {
        fontSize: '14px',
        color: '#64748b',
        margin: '4px 0 0 0',
    },
    // Order Demo
    orderDemoContainer: {
        maxWidth: '600px',
        margin: '0 auto',
    },
    orderSteps: {
        backgroundColor: '#fff',
        borderRadius: '20px',
        padding: '24px',
        marginBottom: '20px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
    },
    orderStep: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '16px 0',
        borderBottom: '1px solid #f1f5f9',
    },
    orderStepNum: {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: '#6366f1',
        color: '#fff',
        fontSize: '14px',
        fontWeight: '700',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    orderStepContent: {
        flex: 1,
    },
    orderStepDemo: {
        display: 'flex',
        gap: '12px',
        marginTop: '8px',
        fontSize: '13px',
        color: '#64748b',
    },
    orderStepNote: {
        margin: '4px 0 0 0',
        fontSize: '13px',
        color: '#10b981',
    },
    toothMini: {
        display: 'flex',
        gap: '6px',
        marginTop: '8px',
    },
    toothSelected: {
        padding: '4px 8px',
        backgroundColor: '#6366f1',
        color: '#fff',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600',
    },
    prostheticMini: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '8px',
        fontSize: '13px',
        color: '#374151',
    },
    orderFeatureGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '12px',
        marginBottom: '24px',
    },
    orderFeatureItem: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '16px 12px',
        textAlign: 'center',
        fontSize: '12px',
        fontWeight: '600',
        color: '#374151',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    },
    featureBenefits: {
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        flexWrap: 'wrap',
    },
    benefitTag: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 18px',
        backgroundColor: '#fff',
        borderRadius: '30px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#6366f1',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    },
    // Matching Demo
    matchingDemo: {
        maxWidth: '600px',
        margin: '0 auto',
    },
    matchingSearch: {
        backgroundColor: '#fff',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '16px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
    },
    matchingSearchHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '16px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '16px',
    },
    matchingFilters: {
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap',
    },
    matchingFilter: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 14px',
        backgroundColor: '#d1fae5',
        color: '#065f46',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '600',
    },
    matchingResults: {
        backgroundColor: '#fff',
        borderRadius: '16px',
        padding: '16px',
        marginBottom: '24px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
    },
    matchingLabCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '14px',
        borderBottom: '1px solid #f1f5f9',
    },
    matchingLabAvatar: {
        width: '44px',
        height: '44px',
        borderRadius: '12px',
        backgroundColor: '#6366f1',
        color: '#fff',
        fontSize: '18px',
        fontWeight: '700',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    matchingLabInfo: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        fontSize: '14px',
    },
    matchingConnectBtn: {
        padding: '8px 16px',
        backgroundColor: '#10b981',
        color: '#fff',
        border: 'none',
        borderRadius: '10px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    matchingBenefits: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
        maxWidth: '600px',
        margin: '0 auto',
    },
    matchingBenefitItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '16px',
        backgroundColor: '#fff',
        borderRadius: '14px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    },
    // Steps
    stepsSection: {
        padding: '50px 24px',
        backgroundColor: '#fff',
    },
    stepsContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        maxWidth: '600px',
        margin: '32px auto 0',
        flexWrap: 'wrap',
    },
    stepCard: {
        position: 'relative',
        backgroundColor: '#f8fafc',
        borderRadius: '16px',
        padding: '24px 20px',
        textAlign: 'center',
        flex: '1 1 150px',
        maxWidth: '180px',
    },
    stepNumber: {
        position: 'absolute',
        top: '-12px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        backgroundColor: '#6366f1',
        color: 'white',
        fontSize: '14px',
        fontWeight: '700',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    stepArrow: {
        display: 'flex',
        alignItems: 'center',
    },
    // Lab Section
    labSection: {
        padding: '50px 24px',
        backgroundColor: '#f8fafc',
    },
    labGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '20px',
        maxWidth: '700px',
        margin: '0 auto',
    },
    labCard: {
        backgroundColor: '#fff',
        borderRadius: '20px',
        padding: '24px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
    },
    labBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        backgroundColor: '#fef3c7',
        color: '#92400e',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        marginBottom: '16px',
    },
    labHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        marginBottom: '16px',
    },
    labAvatar: {
        width: '50px',
        height: '50px',
        borderRadius: '14px',
        backgroundColor: '#eef2ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    labName: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#1e293b',
        margin: 0,
    },
    labStrengths: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        marginBottom: '20px',
    },
    labStrengthItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '14px',
        color: '#374151',
    },
    labCta: {
        padding: '14px',
        backgroundColor: '#eef2ff',
        borderRadius: '12px',
        textAlign: 'center',
        fontSize: '13px',
        color: '#4f46e5',
        lineHeight: 1.5,
    },
    matchingDesc: {
        fontSize: '14px',
        color: '#374151',
        lineHeight: 1.6,
        marginBottom: '16px',
    },
    matchingPreview: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        marginBottom: '20px',
    },
    matchingPreviewItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '14px',
        color: '#065f46',
    },
    // Benefits
    benefitsSection: {
        padding: '50px 24px',
        backgroundColor: '#fff',
    },
    benefitsList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
        maxWidth: '700px',
        margin: '32px auto 0',
    },
    benefitItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        padding: '20px',
        backgroundColor: '#f8fafc',
        borderRadius: '16px',
    },
    // Final CTA
    finalCtaSection: {
        padding: '50px 24px',
        backgroundColor: '#f8fafc',
    },
    finalCtaCard: {
        maxWidth: '500px',
        margin: '0 auto',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: '24px',
        padding: '40px 28px',
        textAlign: 'center',
    },
    finalCtaTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: 'white',
        marginBottom: '12px',
    },
    finalCtaText: {
        fontSize: '15px',
        color: 'rgba(255,255,255,0.9)',
        lineHeight: 1.6,
        marginBottom: '24px',
    },
    finalCtaButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        padding: '18px 36px',
        fontSize: '18px',
        fontWeight: '700',
        color: '#6366f1',
        backgroundColor: 'white',
        border: 'none',
        borderRadius: '14px',
        cursor: 'pointer',
        boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
        marginBottom: '24px',
    },
    finalQrBox: {
        display: 'inline-block',
        padding: '12px',
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: '12px',
    },
    // Footer
    footer: {
        padding: '32px 24px',
        textAlign: 'center',
        backgroundColor: '#1e293b',
    },
    footerText: {
        fontSize: '16px',
        fontWeight: '700',
        color: 'white',
        marginBottom: '6px',
    },
    footerSubtext: {
        fontSize: '13px',
        color: '#94a3b8',
        margin: 0,
    },
};

export default IvanPromoLanding;
